import {useMemo} from 'react';

import {
  FULL_FIDELITY_MODES,
  type FullFidelityMode,
} from '../../components/WorkspaceDrawer/Settings/types';
import {
  type BooleanDynamicConfig,
  type NumericDynamicConfig,
  useViewerOrgsBooleanDynamicConfig,
  useViewerOrgsNumericDynamicConfig,
  useViewerOrgsStringDynamicConfig,
} from '../useDynamicConfig';

export const PRIMARY_LINE_TYPES = [
  'full-fidelity',
  'min-max-last',
  'lttb',
] as const;
export type PrimaryLineType = (typeof PRIMARY_LINE_TYPES)[number];

const VALID_PRIMARY_LINE_TYPES: ReadonlySet<PrimaryLineType> = new Set(
  PRIMARY_LINE_TYPES
);

/**
 * Returns the primary line type configured across all of the viewer's
 * organizations via the `history_line_feature_flags` Statsig dynamic config.
 *
 * The ranking of line types is:
 * - lttb
 * - min-max-last
 * - full-fidelity (default)
 *
 * If any org has the highest priority line type, that wins.
 */
export const usePrimaryLineType = (): PrimaryLineType => {
  const {values, error} = useViewerOrgsStringDynamicConfig(
    'history_line_feature_flags',
    'primaryLineType',
    VALID_PRIMARY_LINE_TYPES
  );

  const value = useMemo(() => {
    if (values.has('lttb')) {
      return 'lttb';
    }
    if (values.has('min-max-last')) {
      return 'min-max-last';
    }
    return 'full-fidelity';
  }, [values]);

  if (error) {
    return 'full-fidelity';
  }

  return value;
};

/**
 * Returns whether min/max/last bin visualization mode is enabled via the
 * `enableMinMaxLastMode` flag on the `history_line_feature_flags` Statsig
 * dynamic config. Enabled for the viewer if ANY of their orgs has it set
 * to `true`.
 */
export const useEnableMinMaxLastMode = (): BooleanDynamicConfig => {
  return useViewerOrgsBooleanDynamicConfig(
    'history_line_feature_flags',
    'enableMinMaxLastMode'
  );
};

/**
 * Derives a `PrimaryLineType` from a `fullFidelityMode` setting value, gated
 * on `enableMinMaxLastMode`. Unlike `usePrimaryLineType` (which aggregates an
 * org-level Statsig config across the viewer's organizations), this hook's
 * input is the `fullFidelityMode` the caller already holds in hand — typically
 * the cascaded panel/workspace setting. Only two values are produced:
 * `'min-max-last'` when the gate is open AND `fullFidelityMode` is explicitly
 * `'min-max-last'`, and `'full-fidelity'` otherwise. The `'lttb'` branch is
 * intentionally out of scope at this call site.
 */
export const useFullFidelityModeLineType = (
  fullFidelityMode: FullFidelityMode | undefined
): PrimaryLineType => {
  const {value: enableMinMaxLastMode} = useEnableMinMaxLastMode();
  if (
    enableMinMaxLastMode &&
    fullFidelityMode === FULL_FIDELITY_MODES.MinMaxLast
  ) {
    return 'min-max-last';
  }
  return 'full-fidelity';
};

/**
 * Returns the maximum `line_plot_max_runs` value configured across all of
 * the viewer's organizations via the `line_plot_increased_max_runs` Statsig
 * dynamic config. Uses the 'max' strategy so the most permissive org wins.
 */
export const useLinePlotMaxRunsDynamicConfig = (): NumericDynamicConfig => {
  return useViewerOrgsNumericDynamicConfig(
    'line_plot_increased_max_runs',
    'line_plot_max_runs',
    'max'
  );
};

/**
 * Returns the maximum `line_plot_max_runs` value for full-screen panels,
 * configured via the `full_screen_line_plot_increased_max_runs` Statsig
 * dynamic config. Uses the 'max' strategy so the most permissive org wins.
 */
export const useFullScreenLinePlotMaxRunsDynamicConfig =
  (): NumericDynamicConfig => {
    return useViewerOrgsNumericDynamicConfig(
      'full_screen_line_plot_increased_max_runs',
      'line_plot_max_runs',
      'max'
    );
  };
